/**
 * 設定 → 帳票デザイン（#78、docs/31、044・045・046）。
 *   1. プリセット（縦3・横2。領収書は標準だけ）と自分の書式（見積書 3・請求書 3・領収書 3）から、それぞれの「会社の既定」を選ぶ（管理者だけ）
 *   2. 自分の書式：枠 1〜3。「新しく作る」（プリセットから起こしてエディタへ）／「修正」（エディタ）／削除（archived。出した紙は変わらない）
 *   3. 顧客ごとの既定は顧客の画面（元請指定の様式など）。出すときは見積書・請求書の紙の画面で選べる
 *   4. 出した紙は書式ごと固めてあるので、ここを変えても変わらない
 * #156（docs/65 §8・★3 a、Figma「設定の残り（10/9）」㉖㉗）で iOS 風に。DB・API・エディタ（/settings/paper/edit）は変えていない
 *   ・一覧（SettingsPaperPage）：節「会社の既定の書式」＝見積書／請求書／領収書の3行（右に今の会社の既定の名前・›）。
 *     オプションを使っていない会社は節「自分の書式」に「帳票デザインのオプション › 使っていません」（/account/plan へ）
 *   ・帳票ごとの画面（SettingsPaperKindPage、/settings/paper/quote・invoice・receipt）：節「プリセット」＝選ぶ行（押した瞬間に PUT /paper/defaults＝会社の既定）。
 *     節「自分の書式」（オプションの会社）＝書式の行（選んでいれば ✓＝会社の既定）＋右に「修正」、「＋ 新しく作る（n／3）」→ シート（元のプリセット）→ エディタ、
 *     削除は「編集」の ⊖ → 確かめ（★4 と同じ作法）
 *   ・担当者は見るだけ（押せない形＋注1行「会社の設定は管理者が変えます」）
 */
import { PAPER_LAYOUT_MSG, PAPER_MSG, REST_MSG, presetOfRef, type PaperDefaultsView, type PaperKind, type PaperLayoutList, type PaperLayoutSummary, type PaperPresetList, type PaperRef } from "@genbaryoku/contract";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router";
import { api, messageOf } from "../api.js";
import { useMe } from "../auth.js";
import { useBackNav, usePageName } from "../components/BackButton.jsx";
import { GroupCheckRow, GroupList, GroupNavRow, GroupRow, GroupSection, GroupTwoLineRow } from "../components/GroupList.jsx";
import { SettingsHead, useRestPageClass } from "../components/SettingsHead.jsx";
import { Sheet } from "../components/Sheet.jsx";
import { PaperThumb } from "../components/PaperThumb.jsx";
import { ErrorText, Help, useToast } from "../components/ui.jsx";
import { hasModule } from "../core/slots.jsx";
import { t } from "../labels.js";

const KINDS = [
  { kind: "quote", title: "見積書" },
  { kind: "invoice", title: "請求書" },
  { kind: "receipt", title: "領収書" },
  // {order}（#233 2d）：{order}を使う会社（モジュール sales）だけ。名前は会社の言葉（t()）
  { kind: "order", title: "{order}" },
] as const;
type Kind = (typeof KINDS)[number]["kind"];
const LIST = "/settings/paper";
const STD: PaperRef = "preset:std-p";

const usePaperData = () => ({
  presets: useQuery({ queryKey: ["paper-presets"], queryFn: () => api<PaperPresetList>("GET", "/paper/presets") }),
  defaults: useQuery({ queryKey: ["paper-defaults"], queryFn: () => api<PaperDefaultsView>("GET", "/paper/defaults") }),
  layouts: useQuery({ queryKey: ["paper-layouts"], queryFn: () => api<PaperLayoutList>("GET", "/paper/layouts") }),
});

/** 会社の既定の書式の名前（プリセットの名前か自分の書式の名前） */
function refName(ref: string, own: Array<PaperLayoutSummary | null>): string {
  if (ref.startsWith("template:")) return own.find((x) => x && `template:${x.id}` === ref)?.name ?? presetOfRef(STD).name;
  return t(presetOfRef(ref).name);
}

/** 「A4・縦・10/5 更新」 */
function ownSub(x: PaperLayoutSummary): string {
  const d = new Date(x.updatedAt);
  return REST_MSG.paperOwnSub
    .replace("{size}", x.pageSize === "custom" ? "自由" : x.pageSize)
    .replace("{orient}", x.orient === "landscape" ? "横" : "縦")
    .replace("{md}", `${d.getMonth() + 1}/${d.getDate()}`);
}

const helpText = () =>
  t("見積書・請求書の見た目（書式）です。ここで決めるのは会社の既定で、{customer}ごとに別の書式を指定したり（{customer}の画面）、発行するときに PDF 確認の画面で選ぶこともできます。発行済みの帳票は書式ごと確定しているので、ここを変えても変わりません。");

export function SettingsPaperPage() {
  const me = useMe();
  const admin = me.data?.user.role === "admin";
  // 戻る（#98、docs/37）：直前の画面があればそこ。無ければ 設定
  const back = useBackNav({ to: "/settings", label: "設定" });
  usePageName(REST_MSG.paperTitle);
  const cls = useRestPageClass("paperset");
  const { presets, defaults, layouts } = usePaperData();

  if (presets.isError) return <ErrorText>{messageOf(presets.error)}</ErrorText>;
  if (defaults.isError) return <ErrorText>{messageOf(defaults.error)}</ErrorText>;
  if (!presets.data || !defaults.data || !layouts.data) return <p className="hint">読み込んでいます…</p>;
  const d = defaults.data;
  const l = layouts.data;

  return (
    <div className={cls} data-testid="paper-settings">
      <SettingsHead back={back} title={REST_MSG.paperTitle} />
      {!admin ? (
        <p className="est-note" id="paperMemberNote">
          {REST_MSG.memberNote}
        </p>
      ) : null}
      <GroupSection title={REST_MSG.paperSectionDefaults} help={<Help testId="help-paper" text={helpText()} />} note={t(REST_MSG.paperDefaultsNote)} data-testid="paper-sec-defaults">
        <GroupList plain>
          {KINDS.filter(({ kind }) => kind !== "order" || hasModule(me.data, "sales")).map(({ kind, title }) => (
            <GroupNavRow key={kind} title={t(title)} value={refName(d[kind] ?? STD, l[kind])} valueTestId={`paper-value-${kind}`} to={`${LIST}/${kind}`} buttonTestId={`paper-row-${kind}`} data-kind={kind} />
          ))}
        </GroupList>
      </GroupSection>
      {!l.available ? (
        <GroupSection title={REST_MSG.paperSectionOwn} note={REST_MSG.paperOptionNote} data-testid="paper-sec-option">
          <GroupList plain>
            <GroupNavRow title={REST_MSG.paperOption} value={REST_MSG.paperOptionOff} to="/account/plan" buttonTestId="paper-option" />
          </GroupList>
        </GroupSection>
      ) : null}
    </div>
  );
}

/**
 * 設定 → 帳票デザイン → 帳票ごとの画面（#156、docs/65 §8、Figma ㉗。/settings/paper/:kind）。
 * PC の2列でも左の印は「帳票デザイン」（contract の settingsRowOf）
 */
export function SettingsPaperKindPage() {
  const { kind: kindParam = "" } = useParams();
  const qc = useQueryClient();
  const nav = useNavigate();
  const me = useMe();
  const admin = me.data?.user.role === "admin";
  const k = KINDS.find((x) => x.kind === kindParam);
  const back = useBackNav({ to: LIST, label: REST_MSG.paperTitle });
  usePageName(k ? t(k.title) : null);
  const cls = useRestPageClass("paperset");
  const { toast, node } = useToast();
  const { presets, defaults, layouts } = usePaperData();
  const [editing, setEditing] = useState(false);
  const [askRemove, setAskRemove] = useState<PaperLayoutSummary | null>(null);
  /** 新しく作るシート：元のプリセット（open でないときは null） */
  const [newFrom, setNewFrom] = useState<{ open: boolean; from: PaperRef }>({ open: false, from: STD });
  const save = useMutation({
    mutationFn: (v: Partial<Record<Kind, PaperRef | null>>) => api<PaperDefaultsView>("PUT", "/paper/defaults", v),
    onMutate: (v) => {
      const prev = qc.getQueryData<PaperDefaultsView>(["paper-defaults"]);
      if (prev) qc.setQueryData(["paper-defaults"], { ...prev, ...v });
      return { prev };
    },
    onSuccess: (v) => {
      qc.setQueryData(["paper-defaults"], v);
      qc.removeQueries({ queryKey: ["estimate"] });
      qc.removeQueries({ queryKey: ["invoice"] });
      qc.removeQueries({ queryKey: ["order"] });
      toast(PAPER_MSG.saved);
    },
    onError: (e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["paper-defaults"], ctx.prev);
      toast(messageOf(e), undefined, "error");
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api<PaperLayoutList>("DELETE", `/paper/layouts/${id}`),
    onSuccess: (v) => {
      qc.setQueryData(["paper-layouts"], v);
      void qc.invalidateQueries({ queryKey: ["paper-presets"] });
      void qc.invalidateQueries({ queryKey: ["paper-defaults"] });
      setAskRemove(null);
      toast(PAPER_LAYOUT_MSG.removed);
    },
    onError: (e) => {
      setAskRemove(null);
      toast(messageOf(e), undefined, "error");
    },
  });

  if (!k || (k.kind === "order" && me.data && !hasModule(me.data, "sales"))) return <Navigate to={LIST} replace />;
  if (presets.isError) return <ErrorText>{messageOf(presets.error)}</ErrorText>;
  if (defaults.isError) return <ErrorText>{messageOf(defaults.error)}</ErrorText>;
  if (!presets.data || !defaults.data || !layouts.data) return <p className="hint">読み込んでいます…</p>;
  const kind: Kind = k.kind;
  const cur = defaults.data[kind] ?? STD;
  const list = presets.data.presets.filter((p) => p.kinds.includes(kind as PaperKind));
  const available = layouts.data.available;
  const slots = layouts.data[kind];
  const own = slots.filter((x): x is PaperLayoutSummary => !!x);
  const emptySlot = slots.findIndex((x) => !x) + 1; // 0＝空きなし
  const canOwn = admin && available;
  const pick = (ref: PaperRef) => {
    if (cur === ref || save.isPending || !admin) return;
    save.mutate({ [kind]: ref === STD ? null : ref });
  };
  const editBtn =
    canOwn && own.length ? (
      <button type="button" className={"btn text cotop-btn" + (editing ? " cotop-done" : "")} onClick={() => setEditing(!editing)} aria-pressed={editing} data-testid="paper-own-edit">
        {editing ? REST_MSG.done : REST_MSG.edit}
      </button>
    ) : null;

  return (
    <div className={cls} data-testid={`paper-${kind}`}>
      <SettingsHead back={back} title={t(k.title)} end={editBtn} />
      {!admin ? (
        <p className="est-note" id="paperMemberNote">
          {REST_MSG.memberNote}
        </p>
      ) : null}
      <GroupSection title={REST_MSG.paperSectionPresets} note={admin ? REST_MSG.paperPresetsNote : undefined} data-testid="paper-sec-presets">
        <GroupList plain>
          {list.map((p) => {
            const ref = `preset:${p.key}` as PaperRef;
            return (
              <GroupCheckRow
                key={p.key}
                title={<><PaperThumb src={p.thumbs[kind]} orient={p.orient} name={t(p.name)} />{t(p.name)}</>}
                sub={t(p.desc)}
                className="grow-paperthumb"
                checked={cur === ref}
                disabled={!admin}
                onChange={() => pick(ref)}
                testId={`paper-${kind}-${p.key}`}
                buttonTestId={`paper-pick-${kind}-${p.key}`}
              />
            );
          })}
        </GroupList>
      </GroupSection>

      {available || own.length ? (
        <GroupSection
          title={REST_MSG.paperSectionOwn}
          help={<Help testId="help-paper-own" text={t("部品を自由に置く書式です。プリセットから起こして、エディタ（パソコンだけ）で 位置・大きさ・文字・線・列・用紙・画像・二次元コード を修正します。{pw:formsFrom}の専用帳票に値だけを印字するときは、{pw:formsFrom}の様式のスキャンを「下敷き」にして枠に合わせ、「用紙」タブの専用帳票モードを入れます。削除しても、発行済みの帳票は変わりません。")} />}
          note={available ? REST_MSG.paperOwnNote : PAPER_LAYOUT_MSG.optionOff}
          data-testid={`paper-own-${kind}`}
        >
          <GroupList plain>
            {own.map((x) => {
              const ref = `template:${x.id}` as PaperRef;
              return editing ? (
                <GroupTwoLineRow
                  key={x.id}
                  title={x.name}
                  sub={ownSub(x)}
                  to={`${LIST}/edit?id=${x.id}`}
                  editing
                  onRemove={() => setAskRemove(x)}
                  removeLabel={REST_MSG.paperRemove.replace("{name}", x.name)}
                  removeTestId={`paper-remove-${kind}-${x.slot}`}
                  testId={`paper-own-${kind}-${x.slot}`}
                />
              ) : (
                <GroupCheckRow
                  key={x.id}
                  title={x.name}
                  sub={ownSub(x)}
                  checked={cur === ref}
                  disabled={!admin}
                  onChange={() => pick(ref)}
                  testId={`paper-own-${kind}-${x.slot}`}
                  buttonTestId={`paper-default-${kind}-${x.slot}`}
                  after={
                    <Link className="glink gchk-after" to={`${LIST}/edit?id=${x.id}`} data-testid={`paper-edit-${kind}-${x.slot}`}>
                      {canOwn ? REST_MSG.paperOwnEdit : REST_MSG.paperOwnView}
                    </Link>
                  }
                />
              );
            })}
            {canOwn && !editing ? (
              <GroupRow
                className="co-addrow"
                icon={
                  <span className="co-plus" aria-hidden="true">
                    <svg viewBox="0 0 24 24">
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                  </span>
                }
                title={REST_MSG.paperOwnNew.replace("{n}", String(Math.min(3, own.length + 1)))}
                onClick={() => setNewFrom({ open: true, from: STD })}
                disabled={!emptySlot}
                buttonTestId={`paper-new-${kind}`}
              />
            ) : null}
          </GroupList>
        </GroupSection>
      ) : null}

      <Sheet
        open={newFrom.open}
        title={REST_MSG.paperNewTitle}
        cancelLabel="取消"
        doneLabel={REST_MSG.create}
        doneDisabled={!emptySlot}
        onCancel={() => setNewFrom({ ...newFrom, open: false })}
        onDone={() => nav(`${LIST}/edit?kind=${kind}&slot=${emptySlot}&from=${encodeURIComponent(newFrom.from)}`)}
        testId="paper-new-sheet"
        doneTestId="paper-new-create"
      >
        <GroupSection title={REST_MSG.paperNewFrom} note={REST_MSG.paperOwnNote}>
          <GroupList plain>
            {list.map((p) => {
              const ref = `preset:${p.key}` as PaperRef;
              return (
                <GroupCheckRow
                  key={p.key}
                  title={<><PaperThumb src={p.thumbs[kind]} orient={p.orient} name={t(p.name)} />{`${t(p.name)}から`}</>}
                  sub={t(p.desc)}
                  className="grow-paperthumb"
                  checked={newFrom.from === ref}
                  onChange={() => setNewFrom({ ...newFrom, from: ref })}
                  buttonTestId={`paper-new-from-${p.key}`}
                />
              );
            })}
          </GroupList>
        </GroupSection>
      </Sheet>

      {askRemove ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && setAskRemove(null)}>
          <div className="dlg" role="alertdialog" aria-label={REST_MSG.paperRemoveAsk} onKeyDown={(e) => e.key === "Escape" && setAskRemove(null)} data-testid="paper-remove-confirm">
            <div className="dh">「{askRemove.name}」を削除しますか</div>
            <div className="db">
              <p style={{ margin: 0 }}>{REST_MSG.paperRemoveBody}</p>
            </div>
            <div className="df">
              <button type="button" className="btn primary" autoFocus onClick={() => setAskRemove(null)}>
                取消
              </button>
              <button type="button" className="btn" style={{ borderColor: "var(--shu)", color: "var(--shu)" }} disabled={remove.isPending} onClick={() => remove.mutate(askRemove.id)} data-testid={`paper-remove-yes-${kind}-${askRemove.slot}`}>
                削除
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {node}
    </div>
  );
}
